<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
  <oxd-dialog
    v-if="!hideDetailForDeleteConfirm"
    class="orangehrm-dialog-modal attendance-cell-detail-dialog"
    :style="{width: '90%', maxWidth: '520px'}"
    @update:show="onClose"
  >
    <div class="orangehrm-modal-header attendance-cell-detail-dialog__header">
      <oxd-text type="card-title" class="attendance-cell-detail-dialog__title">
        {{ employeeName }}
      </oxd-text>
      <oxd-text tag="p" class="attendance-cell-detail-dialog__subtitle">
        {{ $t('attendance.attendance_details') }}
      </oxd-text>
    </div>
    <oxd-divider />
    <div class="attendance-cell-detail-dialog__body">
      <oxd-grid :cols="2" class="attendance-cell-detail-dialog__grid">
        <oxd-text tag="p" class="attendance-cell-detail-dialog__label">
          {{ $t('general.date') }}:
        </oxd-text>
        <oxd-text tag="p" class="attendance-cell-detail-dialog__value">
          {{ formattedDate }}
        </oxd-text>

        <oxd-text tag="p" class="attendance-cell-detail-dialog__label">
          {{ $t('general.attendance') }}:
        </oxd-text>
        <div class="attendance-cell-detail-dialog__value">
          <template v-if="!isEditing">
            <div class="attendance-cell-detail-dialog__value-inline">
              <template v-if="attendanceLines.length">
                <template
                  v-for="(line, lineIndex) in attendanceLines"
                  :key="lineIndex"
                >
                  <span class="attendance-cell-detail-dialog__attendance-line">
                    {{ line }}
                  </span>
                  <br
                    v-if="lineIndex < attendanceLines.length - 1"
                    class="attendance-cell-detail-dialog__attendance-break"
                  />
                </template>
              </template>
              <span
                v-else
                class="attendance-cell-detail-dialog__attendance-line"
              >
                -
              </span>
              <oxd-icon
                v-if="allowEditAttendance"
                class="attendance-cell-detail-dialog__edit-icon"
                name="pencil"
                :class="{'--disabled': isDeleting}"
                @click.stop="startEdit"
              />
              <oxd-icon
                v-if="showDeleteAttendance"
                class="attendance-cell-detail-dialog__delete-icon"
                name="trash"
                :class="{'--disabled': isDeleting || isSaving}"
                @click.stop="confirmDeleteAttendance"
              />
            </div>
          </template>
          <oxd-form v-else :loading="isSaving" @submit-valid="onSave">
            <div
              v-for="(row, rowIndex) in editRows"
              :key="row.id ?? `new-${rowIndex}`"
              class="attendance-cell-detail-dialog__edit-row"
            >
              <oxd-input-field
                v-model="row.punchInTime"
                type="time"
                :label="$t('attendance.punch_in')"
                :disabled="isSaving"
              />
              <oxd-input-field
                v-if="row.hasPunchOut"
                v-model="row.punchOutTime"
                type="time"
                :label="$t('attendance.punch_out')"
                :disabled="isSaving"
              />
              <oxd-text
                v-else
                tag="p"
                class="attendance-cell-detail-dialog__open-punch-hint"
              >
                {{ $t('attendance.punch_out') }}: -
              </oxd-text>
            </div>
            <oxd-form-actions
              class="attendance-cell-detail-dialog__edit-actions"
            >
              <oxd-button
                type="button"
                display-type="ghost"
                :label="$t('general.cancel')"
                :disabled="isSaving"
                @click="cancelEdit"
              />
              <oxd-button
                type="submit"
                display-type="secondary"
                :label="$t('general.save')"
                :loading="isSaving"
              />
            </oxd-form-actions>
          </oxd-form>
        </div>

        <oxd-text tag="p" class="attendance-cell-detail-dialog__label">
          {{ $t('general.leave') }}:
        </oxd-text>
        <div class="attendance-cell-detail-dialog__value">
          <div class="attendance-cell-detail-dialog__value-inline">
            <span class="attendance-cell-detail-dialog__attendance-line">
              {{ leaveLabel }}
            </span>
            <oxd-icon
              v-if="allowEditLeave"
              class="attendance-cell-detail-dialog__edit-icon"
              name="pencil"
              @click.stop="openLeaveEditor"
            />
          </div>
        </div>

        <oxd-text tag="p" class="attendance-cell-detail-dialog__label">
          {{ $t('leave.shift_permission') }}:
        </oxd-text>
        <div class="attendance-cell-detail-dialog__value">
          <div class="attendance-cell-detail-dialog__value-inline">
            <span class="attendance-cell-detail-dialog__attendance-line">
              {{ shiftPermissionLabel }}
            </span>
            <oxd-icon
              v-if="allowEditShiftPermission"
              class="attendance-cell-detail-dialog__edit-icon"
              name="pencil"
              @click.stop="openShiftPermissionEditor"
            />
          </div>
        </div>

        <oxd-text tag="p" class="attendance-cell-detail-dialog__label">
          {{ $t('leave.work_week') }}:
        </oxd-text>
        <oxd-text tag="p" class="attendance-cell-detail-dialog__value">
          {{ workWeekLabel }}
        </oxd-text>

        <oxd-text tag="p" class="attendance-cell-detail-dialog__label">
          {{ $t('leave.holidays') }}:
        </oxd-text>
        <oxd-text tag="p" class="attendance-cell-detail-dialog__value">
          {{ publicHolidayLabel }}
        </oxd-text>
      </oxd-grid>
    </div>
  </oxd-dialog>
  <delete-confirmation ref="deleteDialog" :message="deleteConfirmMessage" />
</template>

<script>
import {OxdDialog, OxdIcon} from '@ohrm/oxd';
import {APIService} from '@/core/util/services/api.service';
import DeleteConfirmationDialog from '@ohrm/components/dialogs/DeleteConfirmationDialog';
import {guessTimezone, timezoneOffsetToHours} from '@/core/util/helper/datefns';
import {navigate} from '@ohrm/core/util/helper/navigation';
import {
  attendanceReportReturnQuery,
  employeeShiftPermissionsFromAttendanceQuery,
} from '@/orangehrmTimePlugin/util/attendanceReportReturnUrl';
import moment from 'moment';

export default {
  name: 'AttendanceCellDetailModal',
  components: {
    'oxd-dialog': OxdDialog,
    'oxd-icon': OxdIcon,
    'delete-confirmation': DeleteConfirmationDialog,
  },
  props: {
    employeeName: {
      type: String,
      required: true,
    },
    dayEntry: {
      type: Object,
      required: true,
    },
    empNumber: {
      type: Number,
      required: true,
    },
    canEditAttendance: {
      type: Boolean,
      default: false,
    },
    canDeleteAttendance: {
      type: Boolean,
      default: false,
    },
    canEditLeave: {
      type: Boolean,
      default: false,
    },
    canEditShiftPermission: {
      type: Boolean,
      default: false,
    },
    reportStartDate: {
      type: String,
      default: null,
    },
    reportEndDate: {
      type: String,
      default: null,
    },
    authEmployeeNumber: {
      type: Number,
      default: null,
    },
  },
  emits: ['close', 'saved'],
  setup() {
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/attendance/reports/records',
    );
    return {http};
  },
  data() {
    return {
      isEditing: false,
      isSaving: false,
      isDeleting: false,
      hideDetailForDeleteConfirm: false,
      deleteConfirmMessage: null,
      editRows: [],
    };
  },
  computed: {
    formattedDate() {
      const dateStr = this.dayEntry?.date;
      if (!dateStr) {
        return '-';
      }
      const m = moment(dateStr, 'YYYY-MM-DD', true);
      if (!m.isValid()) {
        return dateStr;
      }
      const weekday = m.format('dddd');
      const formatted = m.format('DD-MM-YYYY');
      return `${weekday}, ${formatted}`;
    },
    attendanceLines() {
      const punches = Array.isArray(this.dayEntry?.attendances)
        ? this.dayEntry.attendances
        : [];
      return punches.map((punch) => this.formatPunchRange(punch));
    },
    publicHolidayLabel() {
      const name =
        this.dayEntry?.publicHoliday?.name ||
        (this.dayEntry?.leave?.type === 'public-holiday'
          ? this.dayEntry?.leave?.name
          : null);
      return name && String(name).trim() !== '' ? name : '-';
    },
    leaveLabel() {
      const leave = this.dayEntry?.leave;
      if (!leave?.name || leave?.type === 'public-holiday') {
        return '-';
      }
      return String(leave.name).trim() !== '' ? leave.name : '-';
    },
    workWeekLabel() {
      if (this.dayEntry?.isWorkWeekDay === true) {
        return this.$t('general.yes');
      }
      if (this.dayEntry?.isWorkWeekDay === false) {
        return this.$t('general.no');
      }
      return '-';
    },
    defaultTimezone() {
      return guessTimezone();
    },
    allowEditAttendance() {
      if (!this.canEditAttendance) {
        return false;
      }
      if (this.authEmployeeNumber == null) {
        return true;
      }
      return Number(this.empNumber) !== Number(this.authEmployeeNumber);
    },
    deletableRecordIds() {
      const punches = Array.isArray(this.dayEntry?.attendances)
        ? this.dayEntry.attendances
        : [];
      return punches
        .map((punch) => punch?.id)
        .filter((id) => id != null && id !== '');
    },
    showDeleteAttendance() {
      return (
        this.canDeleteAttendance &&
        this.allowEditAttendance &&
        this.deletableRecordIds.length > 0
      );
    },
    allowEditLeave() {
      if (!this.canEditLeave) {
        return false;
      }
      if (this.authEmployeeNumber == null) {
        return true;
      }
      return Number(this.empNumber) !== Number(this.authEmployeeNumber);
    },
    shiftPermissionLabel() {
      const shiftPermission = this.dayEntry?.shiftPermission;
      if (!shiftPermission) {
        return '-';
      }
      const early = shiftPermission.earlyLeaveApproved === true;
      const late = shiftPermission.lateArrivalApproved === true;
      if (early && late) {
        return `${this.$t('leave.early_leave')} & ${this.$t(
          'leave.late_arrival',
        )}`;
      }
      if (early) {
        return this.$t('leave.early_leave');
      }
      if (late) {
        return this.$t('leave.late_arrival');
      }
      return '-';
    },
    hasApprovedShiftPermission() {
      const shiftPermission = this.dayEntry?.shiftPermission;
      return (
        shiftPermission?.earlyLeaveApproved === true ||
        shiftPermission?.lateArrivalApproved === true
      );
    },
    allowEditShiftPermission() {
      if (!this.canEditShiftPermission) {
        return false;
      }
      if (this.authEmployeeNumber == null) {
        return true;
      }
      return Number(this.empNumber) !== Number(this.authEmployeeNumber);
    },
  },
  methods: {
    openLeaveEditor() {
      if (!this.allowEditLeave || !this.dayEntry?.date) {
        return;
      }

      const returnQuery = attendanceReportReturnQuery(
        this.reportStartDate,
        this.reportEndDate,
      );
      const leaveRequestId = this.dayEntry?.leave?.leaveRequestId;

      if (leaveRequestId) {
        navigate(
          '/leave/viewLeaveRequest/{id}',
          {id: leaveRequestId},
          returnQuery,
        );
      } else {
        const query = {
          ...returnQuery,
          empNumber: this.empNumber,
          fromDate: this.dayEntry.date,
          toDate: this.dayEntry.date,
        };
        const leaveTypeId = this.dayEntry?.leave?.leaveTypeId;
        if (leaveTypeId != null) {
          query.leaveTypeId = leaveTypeId;
        }
        navigate('/leave/assignLeave', {}, query);
      }

      this.$emit('close');
    },
    openShiftPermissionEditor() {
      if (!this.allowEditShiftPermission || !this.dayEntry?.date) {
        return;
      }

      const returnQuery = attendanceReportReturnQuery(
        this.reportStartDate,
        this.reportEndDate,
      );

      if (this.hasApprovedShiftPermission) {
        navigate(
          '/leave/viewEmployeeShiftPermissions',
          undefined,
          employeeShiftPermissionsFromAttendanceQuery(
            this.empNumber,
            this.dayEntry.date,
            this.reportStartDate,
            this.reportEndDate,
          ),
        );
      } else {
        navigate('/leave/assignShiftPermission', undefined, {
          ...returnQuery,
          empNumber: this.empNumber,
          leaveDate: this.dayEntry.date,
        });
      }

      this.$emit('close');
    },
    formatPunchRange(punch) {
      const inStr = this.formatPunchTimeDisplay(punch?.in);
      const outValue = punch?.punchOutTime || null;
      const outStr = outValue ? this.formatPunchTimeDisplay(outValue) : '-';
      return `${inStr} - ${outStr}`;
    },
    parsePunchMoment(value) {
      if (!value) {
        return null;
      }
      const m = moment(
        value,
        ['YYYY-MM-DD HH:mm:ss', moment.ISO_8601, 'HH:mm', 'h:mm A'],
        true,
      );
      return m.isValid() ? m : null;
    },
    formatPunchTimeDisplay(value) {
      const m = this.parsePunchMoment(value);
      return m ? m.format('h:mm A') : value ? String(value) : '-';
    },
    formatPunchTimeForInput(value) {
      const m = this.parsePunchMoment(value);
      return m ? m.format('HH:mm') : '';
    },
    buildEditRowFromPunch(punch) {
      const date = this.dayEntry.date;
      const punchInDate = punch.punchInDate || date;
      const punchInTime =
        punch.punchInTime || this.formatPunchTimeForInput(punch.in) || '';
      const punchOutDate = punch.punchOutDate || punchInDate;
      const punchOutTime = punch.punchOutTime || '';
      const tz = this.defaultTimezone;
      const isNew = !punch.id;
      const punchInOffset = timezoneOffsetToHours(
        punch.punchInOffset,
        tz.offset,
      );
      const punchOutOffset = timezoneOffsetToHours(
        punch.punchOutOffset,
        punchInOffset,
      );

      return {
        id: punch.id ?? null,
        punchInDate,
        punchInTime,
        punchOutDate,
        punchOutTime,
        punchInTimezoneName: punch.punchInTimezoneName || tz.name,
        punchInOffset,
        punchOutTimezoneName:
          punch.punchOutTimezoneName || punch.punchInTimezoneName || tz.name,
        punchOutOffset,
        state: punch.state,
        hasPunchOut: true,
        isNew,
        originalPunchInTime: punchInTime,
        originalPunchOutTime: punchOutTime,
      };
    },
    startEdit() {
      if (!this.allowEditAttendance || this.isDeleting) {
        return;
      }
      const punches = Array.isArray(this.dayEntry?.attendances)
        ? this.dayEntry.attendances
        : [];

      if (punches.length > 0) {
        this.editRows = punches.map((punch) =>
          this.buildEditRowFromPunch(punch),
        );
      } else {
        const tz = this.defaultTimezone;
        this.editRows = [
          {
            id: null,
            punchInDate: this.dayEntry.date,
            punchInTime: '',
            punchOutDate: this.dayEntry.date,
            punchOutTime: '',
            punchInTimezoneName: tz.name,
            punchInOffset: tz.offset,
            punchOutTimezoneName: tz.name,
            punchOutOffset: tz.offset,
            state: null,
            hasPunchOut: true,
            isNew: true,
            originalPunchInTime: '',
            originalPunchOutTime: '',
          },
        ];
      }
      this.isEditing = true;
    },
    cancelEdit() {
      this.isEditing = false;
      this.editRows = [];
    },
    buildSavePayload(row) {
      const payload = {
        empNumber: this.empNumber,
        punchInDate: row.punchInDate,
        punchInTime: row.punchInTime,
        punchInTimezoneName: row.punchInTimezoneName,
        punchInOffset: timezoneOffsetToHours(
          row.punchInOffset,
          this.defaultTimezone.offset,
        ),
      };

      const includePunchOut =
        row.isNew || row.hasPunchOut || row.punchOutTime?.trim();

      if (includePunchOut && row.punchOutTime) {
        payload.punchOutDate = row.punchOutDate || row.punchInDate;
        payload.punchOutTime = row.punchOutTime;
        payload.punchOutTimezoneName = row.punchOutTimezoneName;
        payload.punchOutOffset = timezoneOffsetToHours(
          row.punchOutOffset,
          timezoneOffsetToHours(row.punchInOffset, this.defaultTimezone.offset),
        );
      }

      return payload;
    },
    hasRowChanged(row) {
      if (row.isNew) {
        return Boolean(row.punchInTime?.trim());
      }
      if (row.punchInTime !== row.originalPunchInTime) {
        return true;
      }
      if (row.hasPunchOut && row.punchOutTime !== row.originalPunchOutTime) {
        return true;
      }
      return false;
    },
    async confirmDeleteAttendance() {
      if (!this.showDeleteAttendance || this.isDeleting) {
        return;
      }
      const count = this.deletableRecordIds.length;
      this.deleteConfirmMessage =
        count > 1
          ? `${this.$t('general.delete_confirmation_message')} (${count})`
          : `${this.$t('general.delete_confirmation_message')} — ${
              this.employeeName
            }, ${this.formattedDate}`;
      this.hideDetailForDeleteConfirm = true;
      await this.$nextTick();
      let confirmed = 'cancel';
      try {
        confirmed = await this.$refs.deleteDialog?.showDialog();
      } finally {
        this.deleteConfirmMessage = null;
        if (confirmed !== 'ok') {
          this.hideDetailForDeleteConfirm = false;
        }
      }
      if (confirmed !== 'ok') {
        return;
      }
      await this.onDeleteAttendance();
    },
    async onDeleteAttendance() {
      if (!this.showDeleteAttendance) {
        return;
      }
      this.isDeleting = true;
      try {
        await Promise.all(
          this.deletableRecordIds.map((id) => this.http.delete(id)),
        );
        this.$toast.deleteSuccess();
        this.$emit('saved');
        this.onClose();
      } finally {
        this.isDeleting = false;
      }
    },
    async onSave() {
      if (!this.allowEditAttendance) {
        return;
      }
      const rowsToSave = this.editRows.filter((row) => this.hasRowChanged(row));
      if (rowsToSave.length === 0) {
        this.cancelEdit();
        return;
      }

      this.isSaving = true;
      try {
        await Promise.all(
          rowsToSave.map((row) => {
            const payload = this.buildSavePayload(row);
            if (row.isNew) {
              return this.http.create(payload);
            }
            return this.http.update(row.id, payload);
          }),
        );
        this.$toast.updateSuccess();
        this.$emit('saved');
        this.onClose();
      } finally {
        this.isSaving = false;
      }
    },
    onClose() {
      this.cancelEdit();
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss" scoped>
.attendance-cell-detail-dialog {
  &__header {
    flex-direction: column;
    align-items: center;
    gap: 0.25rem;
    margin-bottom: 0.75rem;
  }

  &__title {
    font-family: 'Nunito Sans', sans-serif;
    text-align: center;
  }

  &__subtitle {
    margin: 0;
    font-family: 'Nunito Sans', sans-serif;
    font-size: $oxd-input-control-font-size;
    color: $oxd-input-control-font-color;
    font-weight: 600;
    text-align: center;
  }

  &__body {
    padding: 0.25rem 0.625rem 0.5rem;
  }

  &__grid {
    width: 100%;
    grid-template-columns: minmax(7rem, 38%) 1fr;
    row-gap: 0.75rem;
    column-gap: 0.75rem;
    align-items: start;
  }

  &__value-inline {
    line-height: 1.45;
  }

  &__edit-icon,
  &__delete-icon {
    display: inline-block;
    vertical-align: middle;
    margin-left: 0.35rem;
    cursor: pointer;
    font-size: 0.875rem;
    line-height: 1;

    &:hover {
      opacity: 0.85;
    }

    &.--disabled {
      opacity: 0.4;
      cursor: not-allowed;
      pointer-events: none;
    }
  }

  &__edit-icon {
    color: $oxd-primary-one-color;
  }

  &__delete-icon {
    color: $oxd-feedback-danger-color;
  }

  &__label,
  &__value {
    margin: 0;
    font-family: 'Nunito Sans', sans-serif;
    font-size: $oxd-input-control-font-size;
    line-height: 1.45;
    word-break: break-word;
  }

  &__label {
    font-weight: 700;
    color: $oxd-input-control-font-color;
  }

  &__value {
    font-weight: 400;
    color: $oxd-input-control-font-color;
  }

  &__attendance-line {
    display: inline;
  }

  &__attendance-break {
    display: block;
    margin: 0.25rem 0;
  }

  &__edit-row {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    margin-bottom: 0.5rem;
  }

  &__open-punch-hint {
    margin: 0;
    font-size: $oxd-input-control-font-size;
    color: $oxd-input-control-font-color;
  }

  &__edit-actions {
    justify-content: flex-end;
    margin-top: 0.25rem;
    padding: 0;
  }
}
</style>
